import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Outlet, useLocation, Navigate, useParams } from "react-router-dom";
import { LanguageProvider } from "@/lib/i18n/LanguageContext";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useNoIndex } from "@/hooks/useNoIndex";
import { SEOHead } from "@/components/SEOHead";
import { detectLangFromPath, SLUG_MAP } from "@/hooks/useLocalePath";
import { useEffect, lazy, Suspense } from "react";
import Index from "./pages/Index";

// Lazy-loaded pages for code splitting
const Auth = lazy(() => import("./pages/Auth"));
const Account = lazy(() => import("./pages/Account"));
const Admin = lazy(() => import("./pages/Admin"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Docs = lazy(() => import("./pages/Docs"));
const NotFound = lazy(() => import("./pages/NotFound"));
const SharedProject = lazy(() => import("./pages/SharedProject"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const Contact = lazy(() => import("./pages/Contact"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Generators = lazy(() => import("./pages/Generators"));
const StructuredDataGuide = lazy(() => import("./pages/StructuredDataGuide"));
const GeneratorDetail = lazy(() => import("./pages/GeneratorDetail"));

const queryClient = new QueryClient();

/**
 * Layout route that syncs the URL language prefix with the LanguageContext.
 */
const LanguageSync = () => {
  const location = useLocation();
  const { setLanguage } = useLanguage();

  useEffect(() => {
    const lang = detectLangFromPath(location.pathname);
    setLanguage(lang);
  }, [location.pathname, setLanguage]);

  return (
    <>
      <SEOHead />
      <Outlet />
    </>
  );
};

/**
 * Redirect /en/* → /*
 */
const RedirectFromEn = () => {
  const { "*": rest } = useParams();
  const target = rest ? `/${rest}` : '/';
  return <Navigate to={target} replace />;
};

/**
 * Redirect old English slug under /fr or /es → localized version.
 * e.g. /fr/pricing/* → /fr/tarifs/*
 */
const SlugRedirect = ({ lang, enSlug }: { lang: 'fr' | 'es'; enSlug: string }) => {
  const location = useLocation();
  const { "*": rest } = useParams();
  const localizedSlug = SLUG_MAP[enSlug]?.[lang] || enSlug;
  const target = rest
    ? `/${lang}/${localizedSlug}/${rest}`
    : `/${lang}/${localizedSlug}`;
  return <Navigate to={target + location.search + location.hash} replace />;
};

/** Helper: get the localized slug for a given English slug + language */
const s = (enSlug: string, lang: 'en' | 'fr' | 'es'): string =>
  SLUG_MAP[enSlug]?.[lang] || enSlug;

const LANGUAGES = [
  { prefix: '', lang: 'en' as const },
  { prefix: '/fr', lang: 'fr' as const },
  { prefix: '/es', lang: 'es' as const },
];

// Component to handle noindex logic inside Router context
const AppRoutes = () => {
  useNoIndex();

  return (
    <Suspense fallback={null}>
      <Routes>
        {/* Legacy /en/ redirect → root */}
        <Route path="/en" element={<Navigate to="/" replace />} />
        <Route path="/en/*" element={<RedirectFromEn />} />

        {/* Redirects: old English slugs under /fr and /es → localized versions */}
        {(['fr', 'es'] as const).flatMap(lang =>
          Object.entries(SLUG_MAP)
            .filter(([enSlug, translations]) => translations[lang] !== enSlug)
            .flatMap(([enSlug]) => [
              <Route
                key={`redir-${lang}-${enSlug}`}
                path={`/${lang}/${enSlug}`}
                element={<Navigate to={`/${lang}/${s(enSlug, lang)}`} replace />}
              />,
              <Route
                key={`redir-${lang}-${enSlug}-w`}
                path={`/${lang}/${enSlug}/*`}
                element={<SlugRedirect lang={lang} enSlug={enSlug} />}
              />,
            ])
        )}

        <Route element={<LanguageSync />}>
          {LANGUAGES.flatMap(({ prefix, lang }) => [
            <Route key={`${lang}-index`} path={prefix || '/'} element={<Index />} />,
            <Route key={`${lang}-pricing`} path={`${prefix}/${s('pricing', lang)}`} element={<Pricing />} />,
            <Route key={`${lang}-auth`} path={`${prefix}/${s('auth', lang)}`} element={<Auth />} />,
            <Route key={`${lang}-account`} path={`${prefix}/${s('account', lang)}`} element={<Account />} />,
            <Route key={`${lang}-admin`} path={`${prefix}/admin`} element={<Admin />} />,
            <Route key={`${lang}-docs`} path={`${prefix}/${s('docs', lang)}`} element={<Docs />} />,
            <Route key={`${lang}-terms`} path={`${prefix}/${s('terms', lang)}`} element={<Terms />} />,
            <Route key={`${lang}-privacy`} path={`${prefix}/${s('privacy', lang)}`} element={<Privacy />} />,
            <Route key={`${lang}-project`} path={`${prefix}/${s('projects', lang)}/:id/:slug?`} element={<ProjectDetail />} />,
            <Route key={`${lang}-shared`} path={`${prefix}/${s('shared', lang)}/:token`} element={<SharedProject />} />,
            <Route key={`${lang}-contact`} path={`${prefix}/${s('contact', lang)}`} element={<Contact />} />,
            <Route key={`${lang}-reset-password`} path={`${prefix}/${s('reset-password', lang)}`} element={<ResetPassword />} />,
            <Route key={`${lang}-generators`} path={`${prefix}/${s('generators', lang)}`} element={<Generators />} />,
            <Route key={`${lang}-generator`} path={`${prefix}/${s('generators', lang)}/:type`} element={<Generators />} />,
            <Route key={`${lang}-sd-type`} path={`${prefix}/${s('structured-data', lang)}/:type`} element={<GeneratorDetail />} />,
            <Route key={`${lang}-sd-guide`} path={`${prefix}/${s('structured-data', lang)}`} element={<StructuredDataGuide />} />,
          ])}
        </Route>

        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </TooltipProvider>
    </LanguageProvider>
  </QueryClientProvider>
);

export default App;
